import AuthRoute from "@/components/auth/AuthRoute";
import ProtectedRoute from "@/components/auth/ProtectedRoute";
import AuthLayout from "@/layout/AuthLayout";
import Layout from "@/layout/Layout";
import LoginPage from "@/pages/auth/LoginPage";
import RegisterPage from "@/pages/auth/RegisterPage";
import ResetPasswordPage from "@/pages/auth/ResetPasswordPage";

import Dashboard from "@/pages/dashboard/Dashboard";
import { Routes, Route, useNavigate, useLocation } from "react-router-dom";
import ForgotPasswordPage from "@/pages/auth/ForgotPasswordPage";

import { useEffect } from "react";
import getLink, { LINK_TYPE } from "@/lib/links";
import VerifyEmailPage from "@/pages/auth/VerifyEmailPage";
import GroupPage from "@/pages/group/GroupPage";
import PaymentMethodsPage from "@/pages/paymentmethods/PaymentMethodsPage";
import PaymentMethodPage from "@/pages/paymentmethods/sub/PaymentMethodPage";

import CollectionPage from "@/pages/collection/CollectionPage";
import Account from "@/pages/account/Account";
import DeveloperSettings from "@/pages/accountSettings/DeveloperSettings";
import Users from "@/pages/Admin/Users/Users";
import AdminLayout from "@/layout/AdminLayout";
import Invoices from "@/pages/Admin/Invoices/Invoices";
import Emails from "@/pages/Admin/Emails/Emails";
import SupportFaqs from "@/pages/Admin/SupportFaqs/SupportFaqs";
import Documents from "@/pages/Admin/Documents/Documents";
import { useRbac } from "@/hooks/rbac";
import { useAuthContext } from "@/context/AuthContext";
import { RenderDocument } from "@/components/documents/RenderDocument";
import AddBookingComHotel from "@/pages/Admin/AddBookingComHotel/AddBookingComHotel";
import { RegisterForm } from "@/components/form/RegisterForm";
import { RegisterDetails } from "@/components/form/RegisterDetails";
import Ai from "@/pages/Admin/AI/Ai";
import EmailDetail from "@/pages/Admin/AI/EmailDetail";
import TokenLogin from "@/pages/auth/TokenLogin";
import Ari from "@/pages/Admin/Ari/Ari";
import ArchivedAi from "@/pages/Admin/AI/ArchivedAi";

export function Redirect({ to }: { to: string }) {
  const navigate = useNavigate();
  useEffect(() => {
    navigate(to);
    // console.log("Redirecting to...", to);
  }, []);
  return <></>;
}

const AppRoutes = () => {
  const { isAuthenticated, isVerified, detailsFilled } = useAuthContext();
  const { isSuperAdmin } = useRbac();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    console.log(
      "AppRoutes - useEffect redirect",
      isAuthenticated,
      isVerified,
      isSuperAdmin,
    );
    if (
      isAuthenticated !== null &&
      isVerified !== null &&
      isSuperAdmin !== null
    ) {
      if (!isAuthenticated && location.pathname == "/auth") {
        console.log("Redirect 1");
        return navigate(getLink(LINK_TYPE.LOGIN));
      }
      if (
        isAuthenticated &&
        isVerified === false &&
        location.pathname !== getLink(LINK_TYPE.VERIFY_EMAIL)
      ) {
        console.log("Redirect 2");
        return navigate(getLink(LINK_TYPE.VERIFY_EMAIL));
      }
      if (
        isAuthenticated &&
        location.pathname.includes("/auth") &&
        isVerified === true &&
        !detailsFilled &&
        location.pathname !== getLink(LINK_TYPE.VERIFY_EMAIL)
      ) {
        console.log("Redirect 3");
        return navigate(getLink(LINK_TYPE.REGISTER_USER_DETAILS));
      }
      if (location.pathname === "/" && isSuperAdmin) {
        console.log("Redirect 4");
        return navigate("/admin");
      }
      if (location.pathname.includes("/admin") && !isSuperAdmin) {
        console.log("Redirect 5");
        return navigate(getLink(LINK_TYPE.HOME));
      }
      if (
        location.pathname.includes("/auth") &&
        isAuthenticated &&
        location.pathname !== getLink(LINK_TYPE.VERIFY_EMAIL)
      ) {
        console.log("Redirect 6");
        return navigate(getLink(LINK_TYPE.HOME));
      }
    }
  }, [isSuperAdmin, isAuthenticated, isVerified, location.pathname]);

  return (
    <Routes>
      <Route
        path={getLink(LINK_TYPE.AUTH)}
        element={
          <AuthRoute>
            <AuthLayout />
          </AuthRoute>
        }
      >
        <Route path={getLink(LINK_TYPE.LOGIN)} element={<LoginPage />} />
        <Route path="login-token" element={<TokenLogin />} />
        <Route path={getLink(LINK_TYPE.REGISTER)} element={<RegisterPage />} />
        <Route
          path={getLink(LINK_TYPE.REGISTER_USER)}
          element={<RegisterForm />}
        />
        <Route
          path={getLink(LINK_TYPE.REGISTER_USER_DETAILS)}
          element={<RegisterDetails />}
        />
        <Route
          path={getLink(LINK_TYPE.FORGOT_PASSWORD)}
          element={<ForgotPasswordPage />}
        />
        <Route
          path={getLink(LINK_TYPE.RESET_PASSWORD)}
          element={<ResetPasswordPage />}
        />
        <Route
          path={getLink(LINK_TYPE.VERIFY_EMAIL)}
          element={<VerifyEmailPage />}
        />
        <Route path="" element={<Redirect to={getLink(LINK_TYPE.LOGIN)} />} />
      </Route>

      <Route
        path={getLink(LINK_TYPE.HOME)}
        element={
          <>
            <Layout />
          </>
        }
      >
        <Route
          path={getLink(LINK_TYPE.TOS_DASHBOARD)}
          element={
            <>
              <RenderDocument name="dashboard_tos" />
            </>
          }
        />
        <Route
          path={getLink(LINK_TYPE.PRIVACY_DASHBOARD)}
          element={
            <div>
              <RenderDocument name="dashboard_privacy" />
            </div>
          }
        />
        <Route
          path={getLink(LINK_TYPE.COOKIE_DASHBOARD)}
          element={
            <div>
              <RenderDocument name="Cookie Policy" />
            </div>
          }
        />
      </Route>

      {/* Admin routes */}
      <Route
        path="/admin"
        element={
          <ProtectedRoute>
            <AdminLayout />
          </ProtectedRoute>
        }
      >
        <Route path={getLink(LINK_TYPE.USERS)} element={<Users />} />
        <Route path={getLink(LINK_TYPE.INVOICES)} element={<Invoices />} />
        <Route path={getLink(LINK_TYPE.EMAILS)} element={<Emails />} />
        <Route path={getLink(LINK_TYPE.SUPPORT)} element={<SupportFaqs />} />
        <Route path={getLink(LINK_TYPE.DOCUMENTS)} element={<Documents />} />
        <Route path={getLink(LINK_TYPE.AI)} element={<Ai />} />
        <Route path={getLink(LINK_TYPE.ARCHIVED_AI)} element={<ArchivedAi />} />
        <Route path={getLink(LINK_TYPE.ARI)} element={<Ari />} />
        <Route
          path={`${getLink(LINK_TYPE.AI)}/:emailId`}
          element={<EmailDetail />}
        ></Route>
        <Route path={getLink(LINK_TYPE.USERS)} element={<Users />} />
        <Route
          path={getLink(LINK_TYPE.ADD_BOOKINGCOM_HOTEL)}
          element={<AddBookingComHotel />}
        />
      </Route>

      <Route
        path={getLink(LINK_TYPE.HOME)}
        element={
          <ProtectedRoute>
            <Layout />
          </ProtectedRoute>
        }
      >
        <Route index element={<Dashboard />} />
        <Route
          path={getLink(LINK_TYPE.PAYMENT_METHODS)}
          element={<PaymentMethodsPage />}
        />
        <Route
          path={getLink(LINK_TYPE.PAYMENT_METHOD, {
            __PAYMENTMETHODID__: ":id",
          })}
          element={<PaymentMethodPage />}
        />
        <Route
          path={getLink(LINK_TYPE.DEVELOPER)}
          element={<DeveloperSettings />}
        />
        <Route path={getLink(LINK_TYPE.ACCOUNT)} element={<Account />} />

        {[
          getLink(LINK_TYPE.GROUP, {
            __GROUPID__: ":groupId",
          }),
          getLink(LINK_TYPE.GROUP, {
            __GROUPID__: ":groupId",
          }) + "/*",
        ].map((path, key) => {
          return (
            <Route
              key={"grouppath:" + key}
              path={path}
              element={<GroupPage />}
            ></Route>
          );
        })}

        {[
          getLink(LINK_TYPE.COLLECTION, {
            __COLLECTIONID__: ":collectionId",
          }),
          getLink(LINK_TYPE.COLLECTION, {
            __COLLECTIONID__: ":collectionId",
          }) + "/*",
        ].map((path, key) => {
          return (
            <Route
              key={"collectionpath:" + key}
              path={path}
              element={<CollectionPage />}
            ></Route>
          );
        })}

        <Route path="*" element={<Redirect to={getLink(LINK_TYPE.HOME)} />} />
      </Route>
    </Routes>
  );
};

export default AppRoutes;
